import React, { useState } from 'react';
import { isNil } from 'lodash';

import type { DisplaySpecField } from '~/components/AiChat/types';
import {
  CollapsedText,
  EmptyValue,
  isPlainObject,
  ScalarValue,
} from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/renderFieldValue';
import {
  KeyValueRow,
  type StructuredOutputRendererProps,
} from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/StructuredOutputKeyValue';
import SortableTable from '~/components/core/Tables/SortableTable';
import { Text } from '~/components/core/TextComponents';
import ChevronDownIcon from '~/components/icons/ChevronDownIcon';
import cn from '~/Utils/cn';

// Composites render structurally at the top level and directly inside a `model` group; anything
// deeper — or any composite inside a table cell — collapses to text.
const MAX_STRUCTURAL_DEPTH = 1;

interface SectionValueProps {
  field: DisplaySpecField;
  value: unknown;
}

interface SectionFieldProps extends SectionValueProps {
  depth: number;
}

interface SectionGroupProps {
  label: string;
  // Shown beside the label while the group is collapsed, so a closed group still says how much it
  // holds (a list's item count).
  summary?: string;
  defaultOpen: boolean;
  children: React.ReactNode;
}

interface ModelListTableProps {
  field: DisplaySpecField;
  items: Record<string, unknown>[];
}

// SortableTable reserves row keys of its own (`id` keys the rows and cells, `styles` is spread into
// every cell's style), so each item is wrapped rather than spread: no structured-output key can
// shadow them.
interface ModelListRow {
  id: number;
  item: Record<string, unknown>;
}

const isComposite = (field: DisplaySpecField): boolean => field.type === 'model' || field.type === 'model_list';

// The only `model_list` value a table can lay out: a non-empty list whose every item is an object.
const isRowList = (value: unknown): value is Record<string, unknown>[] =>
  Array.isArray(value) && value.length > 0 && value.every(isPlainObject);

// What a closed list group says about its contents: the item count, or "empty" for an absent or
// empty list. A list the table cannot lay out is left unsummarised rather than miscounted.
const listSummary = (value: unknown): string | undefined => {
  if (isRowList(value)) {
    return `${value.length} ${value.length === 1 ? 'item' : 'items'}`;
  }
  return isNil(value) || (Array.isArray(value) && value.length === 0) ? 'empty' : undefined;
};

const Cell: React.FC<SectionValueProps> = ({ field, value }) =>
  isComposite(field) ? <CollapsedText field={field} value={value} /> : <ScalarValue field={field} value={value} />;

// Every group is collapsible. Top-level groups start closed — a full analysis is long, and the
// scalar rows around them carry the headline — while a group nested inside an opened group starts
// open, so one click reveals a section whole.
const SectionGroup: React.FC<SectionGroupProps> = ({ label, summary, defaultOpen, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div role="group" aria-label={label} className="flex flex-col gap-8 py-12">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full cursor-pointer items-center gap-8 border-0 bg-transparent p-0 text-left text-slate-900"
      >
        <ChevronDownIcon
          size={15}
          iconColor="currentColor"
          className={cn('flex shrink-0 transition-transform', { '-rotate-90': !isOpen })}
        />
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
          {label}
        </Text>
        {summary && !isOpen ? <span className="text-xs text-slate-600">{summary}</span> : null}
      </button>
      {isOpen ? <div className="border-0 border-l-2 border-solid border-slate-500 pl-12">{children}</div> : null}
    </div>
  );
};

const ModelListTable: React.FC<ModelListTableProps> = ({ field, items }) => {
  const columns = (field.fields ?? []).map((child) => ({
    id: child.key,
    label: child.label,
    specialCell: (row: ModelListRow) => <Cell field={child} value={row.item[child.key]} />,
  }));
  const rows: ModelListRow[] = items.map((item, index) => ({ id: index, item }));

  return (
    <div className="overflow-x-auto">
      <SortableTable columns={columns} rows={rows} disableSortByUser keepRowsOrder />
    </div>
  );
};

const SectionField: React.FC<SectionFieldProps> = ({ field, value, depth }) => {
  if (!isComposite(field)) {
    return <KeyValueRow field={field} value={value} wrapAnywhere />;
  }
  if (depth > MAX_STRUCTURAL_DEPTH || (field.type === 'model' && !isNil(value) && !isPlainObject(value))) {
    return (
      <KeyValueRow field={field} value={value} wrapAnywhere>
        <CollapsedText field={field} value={value} />
      </KeyValueRow>
    );
  }
  if (field.type === 'model') {
    // An absent group shows one placeholder, as an absent `model_list` does, not a dash per child.
    return (
      <SectionGroup label={field.label} defaultOpen={depth > 0}>
        {isPlainObject(value) ? (
          <div className="flex flex-col divide-y divide-slate-500">
            {(field.fields ?? []).map((child) => (
              <SectionField key={child.key} field={child} value={value[child.key]} depth={depth + 1} />
            ))}
          </div>
        ) : (
          <EmptyValue />
        )}
      </SectionGroup>
    );
  }

  // model_list
  return (
    <SectionGroup label={field.label} summary={listSummary(value)} defaultOpen={depth > 0}>
      {isRowList(value) ? (
        <ModelListTable field={field} items={value} />
      ) : (
        <CollapsedText field={field} value={value} wrapAnywhere />
      )}
    </SectionGroup>
  );
};

/**
 * `sections` renderer: an insight's possibly-nested structured output. Scalars are key/value rows
 * (as in `key_value`), a `model` field is a labelled indented group of its children, and a
 * `model_list` field is a table whose columns are the child fields, one row per item. Groups are
 * collapsible; top-level ones start closed.
 */
export const StructuredOutputSections: React.FC<StructuredOutputRendererProps> = ({
  structured_output,
  display_spec,
}) => (
  <div className="flex flex-col divide-y divide-slate-500 rounded-xl bg-white px-16">
    {display_spec.fields.map((field) => (
      <SectionField key={field.key} field={field} value={structured_output[field.key]} depth={0} />
    ))}
  </div>
);
